import { Link, useParams } from 'react-router-dom';
import { Archive } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { useT } from '@/lib/i18n/useT';
import { XotskisTab } from './XotskisTab';

/**
 * Standalone Xotskies catalog for a single brand.
 *
 * Renders the SAME surface as the brand profile's "Xotskies" tab, but on its
 * own page — so the sidebar's "My Xotskies" entry shows ONLY the catalog
 * (grid + agency adopt section), without the brand hero, KPIs or sibling
 * tabs. Brand-scoped via the `:id` route param; `agencyId` is the parent
 * agency, mirroring how BrandProfilePage wires `<XotskisTab>`.
 */
export function BrandXotskiesPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const brandId = Number(id);
  const validId = Number.isFinite(brandId) && brandId > 0;

  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId),
    queryFn: () => brandsApi.get(brandId),
    enabled: validId,
  });
  const brand = brandQ.data ?? null;

  return (
    <div className="page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Breadcrumbs
          items={[
            { label: brand?.name ?? t('breadcrumbs.brand'), to: `/brand/${brandId}` },
            { label: t('sidebar.myXotskies') },
          ]}
        />
        {/* Lo que quedó en las estaciones de eventos ya terminados: se cierra
            desde cada evento y vuelve aquí, al almacén. */}
        {validId && (
          <Link
            to={`/brand/${brandId}/leftovers`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border-default)] px-3 py-1.5 text-xs font-bold text-[var(--color-fg-muted)] hover:border-[var(--color-accent-ink)] hover:text-[var(--color-accent-ink)]"
          >
            <Archive className="h-3.5 w-3.5" />
            {t('brand.tabs.leftovers')}
          </Link>
        )}
      </div>
      <XotskisTab
        brandId={validId ? brandId : null}
        agencyId={brand?.parentId ?? null}
      />
    </div>
  );
}
